import React from "react";
import { ShieldCheck, Users, Briefcase, BarChart, Cpu, ArrowRight } from "lucide-react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { InView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

const stats = [
  { label: "Families Trusting Us", value: 10000 },
  { label: "Stocks Analyzed", value: 5000 },
  { label: "Years of Data", value: 25 },
  { label: "Client Locations", value: 1000 },
];

const ClientsWhotrustus="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/ClientsWhotrustus.png";
const OurCoreValues="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/OurCoreValues.png";
const ManagementProfile="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/ManagementProfile.png";
const ResearchSpan="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/ResearchSpan.png";
const Technology="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/Technology.png";
const arrow="https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/home/arrow.png";

// Enhanced card data with highlighted numbers
const cardData = [
  {
    title: "Our Core Values",
    icon: ShieldCheck,
    items: [
      "Client first",
      "100% transparent",
      "0 conflict of interest",
      "No hidden fee",
    ],
    image: OurCoreValues,
  },
  {
    title: "Clients Who Trust Us",
    icon: Users,
    items: [
      "10,000+ families",
      "Empanelled at Large Corporates",
      "Clients from 1000+ locations",
    ],
    image: ClientsWhotrustus,
  },
  {
    title: "Management Profile",
    icon: Briefcase,
    items: [
      "IIM, IIT, CA Ranker background",
      "Decades of Experience",
      "Global & Diverse Industry exposure",
    ],
    image: ManagementProfile,
  },
  {
    title: "Research Span & Technology",
    icon: BarChart,
    items: [
      "5000+ stocks scanned",
      "1500+ mutual funds researched",
      "25+ years of data",
      "AI-Driven Portfolio Construction",
    ],
    image: ResearchSpan,
  },
  {
    title: "Technology Powered Solutions",
    icon: Cpu,
    items: [
      "Generative AI research",
      "Machine Learning algorithms",
      "Real-time market analysis",
      "Automated risk management",
    ],
    image: Technology,
  },
];

// Enhanced Card component that highlights numerical content
const Card = ({ title, icon: Icon, items, delay, image, zoomImage }) => {
  const navigate = useNavigate();
  
  // Function to render an item based on whether it's a string or an object with highlighted content
  const renderItem = (item, inView) => {
    if (typeof item === 'string') {
      return item;
    }
    
    const { text, highlight } = item;
    const parts = text.split(highlight);
    
    // Extract number from highlight if it exists
    const number = highlight.match(/\d+/);
    
    return (
      <>
        {parts[0]}
        <span className="font-extrabold text-blue-900">
          {number ? highlight : highlight}
        </span>
        {parts[1]}
      </>
    );
  };

  const isLastCard = items.includes("Automated risk management"); // Check if this is the last card

  return (
    <InView>
      {({ inView }) => (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay }}
          className="bg-white rounded-3xl overflow-hidden w-full h-full flex flex-col border border-indigo-100 hover:ring-2 hover:ring-indigo-100"
        >
          <div className="relative h-52 w-full">
            <img
              src={image}
              alt={title}
              className={`absolute inset-0 w-full h-full object-cover object-center ${zoomImage ? 'lg:scale-[1.2]' : ''}`}
              style={zoomImage ? { transition: 'transform 0.3s' } : {}}
            />
          </div>
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Icon className="w-6 h-6 text-indigo-600" />
                <h3 className="text-lg font-bold text-gray-900 leading-tight">
                  {title}
                </h3>
              </div>
              <ul className="text-gray-700 space-y-3 text-[15px] font-medium leading-snug">
                {items.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <img 
                      src={arrow} 
                      alt="Bullet point" 
                      className="w-2 h-2"
                    />
                    <span className="flex-1">{renderItem(item, inView)}</span>
                  </li>
                ))}
              </ul>
            </div>
            {isLastCard && (
              <div 
                className="text-blue-600 hover:text-blue-800 transition-colors cursor-pointer mt-4 flex items-center space-x-1"
                onClick={() => navigate('/ai-driven')}
              >
                <span>Learn More</span>
                <ArrowRight 
                  className="h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200 cursor-pointer transition-all" 
                  onClick={() => navigate('/ai-driven')}
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </InView>
  );
};

export default function HighlightSection() {
  return (
    <div id="highlight" className="bg-white w-full">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 relative overflow-hidden">
        <div className="absolute w-[500px] h-[500px] bg-indigo-200 opacity-30 blur-[140px] top-0 left-1/2 -translate-x-1/2 rounded-full z-0"></div>

        {/* Numbers First */}
        <div className="text-center max-w-6xl mx-auto mb-12 lg:mb-24 z-10 relative">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4"
          >
            Trust is the Only Currency We Pursue
          </motion.h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-12 pt-8 lg:pt-10">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                className="text-center"
              >
                <h3 className="text-3xl md:text-4xl lg:text-6xl font-black text-blue-900 drop-shadow-sm">
                  <CountUp end={stat.value} duration={2.5} separator="," />
                  <span className="text-blue-900">+</span>
                </h3>
                <p className="text-gray-600 mt-1 lg:mt-2 text-sm lg:text-md font-medium">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Cards - Single column on mobile/tablet, original layout on desktop */}
        <div className="z-10 relative">
          {/* Mobile & Tablet View - Single Column */}
          <div className="block lg:hidden">
            <div className="grid grid-cols-1 gap-6">
              {cardData.map((card, index) => (
                <div key={index} className="w-full min-h-[450px]">
                  <Card {...card} delay={index * 0.2} />
                </div>
              ))}
            </div>
          </div>
          
          {/* Desktop View - Original Layout */}
          <div className="hidden lg:block">
            {/* Row 1 */}
            <div className="grid grid-cols-3 gap-6 place-items-stretch mb-10">
              <div className="min-h-[450px]">
                <Card {...cardData[0]} delay={0} />
              </div>
              <div className="min-h-[450px]">
                <Card {...cardData[1]} delay={0.2} />
              </div>
              <div className="min-h-[450px]">
                <Card {...cardData[2]} delay={0.4} />
              </div>
            </div>
            
            {/* Row 2 - Centered */}
            <div className="flex justify-center gap-20 flex-wrap">
              <div className="w-96 min-h-[450px]">
                <Card {...cardData[3]} delay={0.6} zoomImage />
              </div>
              <div className="w-96 min-h-[450px]">
                <Card {...cardData[4]} delay={0.8} zoomImage />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}